/**
 * Created by andrey on 21.08.2025
 */

const HowlerAudioEngine = function () {
    this.sounds = {};
    this.unloadTimes = {};
    this.playIds = {};

    cc.loader.register(["mp3", "ogg", "wav", "mp4", "m4a"], {
        load: (realUrl, url, res, cb) => {
            this.load(realUrl, url, res, cb);
        }
    });
};

HowlerAudioEngine.prototype.playMusic = function (url) {
    this.stopMusic();

    const music = this.sounds[url];
    const loaded = music && music.state() === "loaded";

    if (!loaded) {
        return;
    }

    // howler parks a play() made while the context is suspended on a one-shot "resume" listener and
    // fires it on the player's first click - by then the scene that asked for the track has usually
    // moved on, so a stale disk blips before the next transition ejects it. Skip the play like
    // playSound does and let resumeAudioContext start whatever disk is current instead.
    if (!this.isAudioContextRunning()) {
        return;
    }

    this.music = music;
    this.musicUrl = url;

    const options = this.fadeOptions;
    const volume = options ? options.from : 1.0;

    music.loop(true);
    music.volume(volume);
    // the id is what makes a later play() resume from the paused position - play() without one
    // recycles an ended sound and starts the track over
    this.musicId = music.play();

    if (options) {
        this.fade(options.duration, options.from, options.to);
        delete this.fadeOptions;
    }
};

HowlerAudioEngine.prototype.stopMusic = function () {
    if (this.music) {
        this.music.off("resume");
        this.music._playLock = false;
        this.music.stop();
        this.music._queue.length = 0;
        this.music = undefined;
        this.musicId = undefined;
        this.musicUrl = undefined;
    }

    this.musicPaused = false;

    delete this.fadeOptions;
};

HowlerAudioEngine.prototype.musicState = function () {
    const music = this.music;
    const context = Howler.ctx;
    const parts = [`howler=${Howler.state}`, `ctx=${context && context.state}`];

    if (!music) {
        parts.push("music=none");
        return parts.join(" ");
    }

    parts.push(
        `src=${music._src}`,
        `state=${music.state()}`,
        `playLock=${music._playLock}`,
        `queue=[${music._queue.map((task) => task.event)}]`,
        `onresume=${music._onresume.length}`,
        `playing=${music.playing()}`,
        `sounds=${music._sounds.length}`,
        `stale=${this.sounds[this.musicUrl] !== music}`
    );

    const sound = music._sounds[0];
    if (sound) {
        parts.push(`sound=paused:${sound._paused},ended:${sound._ended},node:${Boolean(sound._node)}`);
    }

    const unloadTime = this.unloadTimes[this.musicUrl];
    if (unloadTime) {
        parts.push(`unloadedAgo=${Math.round((Date.now() - unloadTime) / 1000)}s`);
    }

    return parts.join(" ");
};

HowlerAudioEngine.prototype.isMusicSilence = function () {
    return this.music && !this.musicPaused && !this.music.playing() && !this.music._playLock;
};

HowlerAudioEngine.prototype.checkMusicSilence = function () {
    if (!this.isMusicSilence()) {
        return;
    }

    // a play() parked on once("resume") can lose its listener without ever starting - nothing
    // retries after that, so restart the music instead of staying silent forever
    this.playMusic(this.musicUrl);

    cleverapps.timeouts.setTimeout(() => this.reportMusicSilence(), 1000);
};

HowlerAudioEngine.prototype.reportMusicSilence = function () {
    if (!this.isMusicSilence() || this.silenceReported) {
        return;
    }
    this.silenceReported = true;

    const message = `Music silent: ${this.musicState()}`;
    if (cleverapps.config.debugMode) {
        throw new Error(message);
    } else {
        cleverapps.throwAsync(message);
    }
};

HowlerAudioEngine.prototype.pauseMusic = function () {
    const music = this.music;
    if (!music || !music.playing(this.musicId)) {
        return false;
    }

    music.pause(this.musicId);
    this.musicPaused = true;
    return true;
};

// true means the track is left playing - false, that nothing is held and the caller decides
// what to play next
HowlerAudioEngine.prototype.resumeMusic = function () {
    const music = this.music;
    const paused = this.musicPaused;
    this.musicPaused = false;

    if (!music || !paused || !this.isAudioContextRunning()) {
        return false;
    }

    if (!music.playing(this.musicId)) {
        music.play(this.musicId);
    }

    return true;
};

HowlerAudioEngine.prototype.mute = function () {
    Howler.mute(true);
};

HowlerAudioEngine.prototype.unmute = function () {
    Howler.mute(false);
};

HowlerAudioEngine.prototype.isPlaying = function (url, spriteName) {
    const sound = this.sounds[url];
    if (!sound) {
        return false;
    }

    if (spriteName) {
        return this.listPlayIds(url, spriteName).length > 0;
    }

    return sound.playing();
};

HowlerAudioEngine.prototype.listPlayIds = function (url, spriteName) {
    const sound = this.sounds[url];
    const ids = this.playIds[url] && this.playIds[url][spriteName];
    if (!sound || !ids) {
        return [];
    }

    const playing = ids.filter((id) => sound.playing(id));
    this.playIds[url][spriteName] = playing;
    return playing;
};

HowlerAudioEngine.prototype.isLoaded = function (url) {
    // console.log("isLoaded", this.sounds[url], this.sounds[url] && this.sounds[url].state());
    return this.sounds[url] && this.sounds[url].state() === "loaded";
};

HowlerAudioEngine.prototype.playSound = function (url, spriteName, options) {
    if (!this.isLoaded(url)) {
        return;
    }

    options = options || {};

    const sound = this.sounds[url];

    // console.log("play sound", url, sound);
    // console.log("play sound", url, sound.state());

    if (options.loop) {
        sound.loop(options.loop);
    }

    const id = sound.play(spriteName);
    if (spriteName && id !== null) {
        this.playIds[url] = this.playIds[url] || {};
        this.playIds[url][spriteName] = this.listPlayIds(url, spriteName).concat(id);
    }

    this.checkMusicSilence();
};

HowlerAudioEngine.prototype.stopSound = function (url, spriteName) {
    const sound = this.sounds[url];
    if (!sound) {
        return;
    }

    if (spriteName) {
        this.listPlayIds(url, spriteName).forEach((id) => sound.stop(id));
        return;
    }

    if (sound.playing()) {
        sound.stop();
    }
};

HowlerAudioEngine.prototype.isAudioContextRunning = function () {
    if (connector.platform.oneOf(connector.WECHAT)) {
        return true;
    }

    const context = Howler.ctx;
    return context && context.state === "running";
};

HowlerAudioEngine.prototype.resumeAudioContext = function (onResumed) {
    const context = Howler.ctx;
    if (!context) {
        return;
    }

    // Howler.state is a cached copy of context.state updated only in howler's own promise
    // callbacks - resuming the raw context directly leaves it stale, and play() then parks
    // forever on the Howler.state check. Resume through howler so the two stay in sync and
    // parked plays get their "resume" event.
    if (Howler.state === "running" && context.state === "suspended") {
        Howler.state = "suspended";
    }

    const wasRunning = this.isAudioContextRunning();

    Howler._autoResume();

    // only the suspended -> running edge is interesting: a click on an already running context
    // must not restart the track that is playing
    if (onResumed && !wasRunning) {
        context.resume().then(onResumed);
    }
};

HowlerAudioEngine.prototype.loadState = function (url) {
    const sound = this.sounds[url];
    const context = Howler.ctx;

    const parts = [
        `state=${sound && sound.state()}`,
        `html5=${sound && sound._html5}`,
        `webaudio=${Howler.usingWebAudio}`,
        `ctx=${context && context.state}`,
        `mp3=${Howler.codecs("mp3")}`,
        `hidden=${document.hidden}`
    ];

    // how far a slow load got: bytes of the web audio request, network and ready state of the html5 element
    if (sound && sound._xhrLoaded !== undefined) {
        parts.push(`received=${sound._xhrLoaded}/${sound._xhrTotal}`);
    }

    const node = sound && sound._html5 && sound._sounds[0] && sound._sounds[0]._node;
    if (node) {
        parts.push(`node=network:${node.networkState},ready:${node.readyState}`);
    }

    return parts.join(" ");
};

HowlerAudioEngine.prototype.load = function (realUrl, url, res, callback) {
    const timeoutError = { type: "sound", status: cc.LOAD_TIMEOUT_STATUS };
    const timeout = res instanceof VirtualUrl && !res.isImportant() ? HowlerAudioEngine.BACKGROUND_SOUND_TIMEOUT : cc.LOAD_TIMEOUT;
    const onEnd = callback || (() => {});
    callback = cleverapps.waitNoMore(timeout, (error, loaded) => {
        if (error === timeoutError) {
            error.errorMessage = `load sound timeout ${this.loadState(url)}`;

            // drop the stuck request, otherwise the retry waits for it again
            this.unload(url);
        }
        onEnd(error, loaded);
    }, timeoutError);

    let sound = this.sounds[url];
    const sprite = res instanceof VirtualUrl ? res.soundFrames : undefined;

    const onload = () => {
        // console.log(`sound ${url} load success`, sound);
        callback(undefined, sound);
    };

    // howler passes the reason: xhr status, decoding error, or the html5 MediaError with the web audio failure that led to html5
    const onloaderror = (id, error) => {
        callback({ type: "sound", status: cc.LOAD_ERROR_STATUS, errorMessage: `${error || "load sound failed"} ${this.loadState(url)}` });
    };

    if (sound && sound.state() === "loaded") {
        onload();
        return;
    }

    if (sound && sound.state() === "loading") {
        sound.once("load", onload);
        sound.once("loaderror", onloaderror);
        return;
    }

    this.unload(url);

    sound = this.sounds[url] = new Howl({
        src: [cc.loader.getRequestUrl(realUrl)],
        preload: false,
        sprite
    });

    sound.once("load", () => {
        onload();
        sound.off("loaderror");
    });
    sound.once("loaderror", (id, error) => {
        onloaderror(id, error);
        sound.off("load");

        // howler leaves a failed sound in the "loading" state - drop it, otherwise the retry waits for it until the timeout
        this.unload(url);
    });

    sound.load();
};

HowlerAudioEngine.prototype.unload = function (url) {
    const sound = this.sounds[url];
    if (sound) {
        this.unloadTimes[url] = Date.now();

        sound.off();
        sound.unload();
        delete this.sounds[url];
        delete this.playIds[url];
    }
};

HowlerAudioEngine.prototype.fade = function (duration, from, to) {
    delete this.fadeOptions;

    if (this.music) {
        this.music.fade(from, to, duration * 1000);
    } else {
        this.fadeOptions = { duration, from, to };
    }
};

// a sound nothing waits for is small (2-165 KB) - give up early instead of holding a request for the full loader timeout
HowlerAudioEngine.BACKGROUND_SOUND_TIMEOUT = 15000;
